import { useState } from 'react';
import {
  Search, Filter, Mail, Phone, Video, FileText, MessageSquare,
  ChevronDown, ChevronUp, Inbox,
} from 'lucide-react';
import { useInteractions, type InteractionFilters, type Interaction } from '../hooks/useInteractions';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface InteractionsViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const channelOptions = ['', 'email', 'phone', 'meeting', 'note'];

const channelIcons: Record<string, typeof Mail> = {
  email: Mail,
  phone: Phone,
  meeting: Video,
  note: FileText,
  other: MessageSquare,
};

const channelColors: Record<string, string> = {
  email: 'bg-blue-100 text-blue-600',
  phone: 'bg-green-100 text-green-600',
  meeting: 'bg-purple-100 text-purple-600',
  note: 'bg-yellow-100 text-yellow-600',
  other: 'bg-slate-100 text-slate-600',
};

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getDateGroup(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const today = now.toDateString();
  const yesterday = new Date(now.getTime() - 86_400_000).toDateString();
  const dateDay = date.toDateString();

  if (dateDay === today) return 'Today';
  if (dateDay === yesterday) return 'Yesterday';
  const diffDays = Math.floor((now.getTime() - date.getTime()) / 86_400_000);
  if (diffDays <= 7) return 'This Week';
  return 'Earlier';
}

const groupOrder = ['Today', 'Yesterday', 'This Week', 'Earlier'];

export function InteractionsView({ onNavigate }: InteractionsViewProps) {
  const [filters, setFilters] = useState<InteractionFilters>({ limit: 50 });
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const queryFilters: InteractionFilters = {
    ...filters,
    search: searchText || undefined,
  };

  const { data, isLoading, error } = useInteractions(queryFilters);

  const interactions = data?.interactions ?? [];

  // Group by date
  const grouped = new Map<string, Interaction[]>();
  for (const item of interactions) {
    const group = getDateGroup(item.createdAt);
    if (!grouped.has(group)) grouped.set(group, []);
    grouped.get(group)!.push(item);
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Interactions</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} interactions` : 'Email and activity timeline'}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          <form onSubmit={(e) => e.preventDefault()} className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search interactions..."
              className="input pl-9"
            />
          </form>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={cn('btn-secondary btn-sm', showFilters && 'bg-brand-50 border-brand-200 text-brand-700')}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="mt-3 flex items-center gap-3">
            <div>
              <label className="label mb-1">Channel</label>
              <select
                className="input py-1.5"
                value={filters.channel ?? ''}
                onChange={(e) => setFilters((f) => ({ ...f, channel: e.target.value || undefined }))}
              >
                <option value="">All Channels</option>
                {channelOptions.filter(Boolean).map((c) => (
                  <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Timeline */}
      <div className="p-6">
        {error ? (
          <div className="card p-8 text-center text-sm text-red-600">
            Failed to load interactions. Please try again.
          </div>
        ) : isLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="h-8 w-8 animate-pulse rounded-full bg-surface-200" />
                <div className="flex-1">
                  <div className="h-4 w-48 animate-pulse rounded bg-surface-200" />
                  <div className="mt-1 h-3 w-72 animate-pulse rounded bg-surface-100" />
                </div>
              </div>
            ))}
          </div>
        ) : interactions.length > 0 ? (
          <div className="space-y-6">
            {groupOrder.map((groupName) => {
              const items = grouped.get(groupName);
              if (!items || items.length === 0) return null;

              return (
                <div key={groupName}>
                  <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                    {groupName}
                  </h3>
                  <div className="space-y-2">
                    {items.map((item) => {
                      const Icon = channelIcons[item.channel] ?? channelIcons.other;
                      const iconColor = channelColors[item.channel] ?? channelColors.other;
                      const expanded = expandedId === item.id;

                      return (
                        <div key={item.id} className="card overflow-hidden">
                          <button
                            type="button"
                            className="flex w-full items-start gap-3 p-4 text-left hover:bg-surface-50"
                            onClick={() => setExpandedId(expanded ? null : item.id)}
                          >
                            <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-full', iconColor)}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-slate-900 truncate">
                                  {item.subject || item.channel}
                                </span>
                                <Badge color="gray" size="sm">{item.channel}</Badge>
                                {item.direction && (
                                  <Badge color={item.direction === 'inbound' ? 'blue' : 'green'} size="sm">
                                    {item.direction}
                                  </Badge>
                                )}
                              </div>
                              <div className="mt-0.5 text-xs text-slate-500 truncate">
                                {item.fromAddress && <span>From: {item.fromAddress}</span>}
                                {item.fromAddress && item.toAddress && <span> &rarr; </span>}
                                {item.toAddress && <span>{item.toAddress}</span>}
                              </div>
                              {item.preview && (
                                <p className="mt-1 text-xs text-slate-400 line-clamp-2">{item.preview}</p>
                              )}
                            </div>
                            <div className="flex shrink-0 flex-col items-end gap-1">
                              <span className="text-2xs text-slate-400">{formatTime(item.createdAt)}</span>
                              {expanded ? (
                                <ChevronUp className="h-4 w-4 text-slate-400" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-slate-400" />
                              )}
                            </div>
                          </button>

                          {expanded && item.body && (
                            <div className="border-t border-surface-100 bg-surface-50 p-4">
                              <div className="prose prose-sm max-w-none text-sm text-slate-600 whitespace-pre-wrap">
                                {item.body}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState
            icon={Inbox}
            title="No interactions"
            description="Interactions from emails, calls, and meetings will appear here."
          />
        )}
      </div>
    </div>
  );
}
